import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Wifi, WifiOff, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check localStorage for dismissed state
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-xl shadow-2xl border border-purple-100 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Smartphone className="w-6 h-6 text-purple-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">Install App</h3>
          <p className="text-sm text-gray-600 mt-1">
            Add to your home screen for quick access and offline support
          </p>
          <Button
            onClick={handleInstall}
            className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white"
            size="sm"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
        </div>
      </div>
    </div>
  );
}

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    // Initialize and get pending count
    import('../lib/offlineStorage').then(module => {
      const offlineStorage = module.default;
      offlineStorage.init().then(() => {
        offlineStorage.getPendingSyncCount().then(counts => {
          setPendingSyncCount(counts.total);
        });
      });
    });

    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      // Auto-sync when coming back online
      handleAutoSync();
      setTimeout(() => {
        if (pendingSyncCount === 0) setShowBanner(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending count periodically
    const interval = setInterval(async () => {
      try {
        const module = await import('../lib/offlineStorage');
        const counts = await module.default.getPendingSyncCount();
        setPendingSyncCount(counts.total);
        // Hide banner if online and no pending items
        if (navigator.onLine && counts.total === 0) {
          setShowBanner(false);
        }
      } catch (e) {
        // Ignore errors
      }
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [pendingSyncCount]);

  const handleAutoSync = async () => {
    setIsSyncing(true);
    try {
      const module = await import('../lib/offlineStorage');
      await module.default.syncAllQueues();
      const counts = await module.default.getPendingSyncCount();
      setPendingSyncCount(counts.total);
    } catch (e) {
      console.error('Auto-sync failed:', e);
    }
    setIsSyncing(false);
  };

  if (!showBanner && pendingSyncCount === 0) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-sm font-medium transition-all ${
        isOnline
          ? pendingSyncCount > 0
            ? 'bg-amber-500 text-white'
            : 'bg-green-500 text-white'
          : 'bg-red-500 text-white'
      }`}
      data-testid="offline-indicator"
    >
      <div className="flex items-center justify-center gap-2">
        {isOnline ? (
          pendingSyncCount > 0 ? (
            <>
              {isSyncing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Syncing {pendingSyncCount} items...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4" />
                  {pendingSyncCount} items pending sync
                </>
              )}
            </>
          ) : (
            <>
              <Wifi className="w-4 h-4" />
              Back online - All synced
            </>
          )
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            You're offline - Changes will sync when you're back online
            {pendingSyncCount > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs ml-2">
                {pendingSyncCount} saved
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}
